(() => {
  const STORAGE_KEY = 'also-and-perhaps:favorites:v1';
  const MAX_ITEMS = 100;
  const productCache = new Map();

  const cleanText = (value, limit = 240) => String(value || '').trim().slice(0, limit);
  const cleanUrl = (value) => {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      return url.origin === window.location.origin ? `${url.pathname}${url.search}` : '';
    } catch (_error) {
      return '';
    }
  };
  const cleanImage = (value) => {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      return url.protocol === 'https:' || url.origin === window.location.origin ? url.href : '';
    } catch (_error) {
      return '';
    }
  };
  const cleanItem = (item) => {
    const id = cleanText(item && item.id, 80);
    const url = cleanUrl(item && item.url);
    if (!id || !url) return null;
    return {
      id,
      title: cleanText(item.title),
      url,
      image: cleanImage(item.image),
      price: cleanText(item.price, 80),
      color: cleanText(item.color, 80),
    };
  };
  const read = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.map(cleanItem).filter(Boolean).slice(0, MAX_ITEMS);
    } catch (_error) {
      return [];
    }
  };
  const write = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch (_error) {
      return false;
    }
    document.dispatchEvent(new CustomEvent('ap:wishlist:change'));
    return true;
  };
  const fromButton = (button) => cleanItem({
    id: button.dataset.productId,
    title: button.dataset.productTitle,
    url: button.dataset.productUrl,
    image: button.dataset.productImage,
    price: button.dataset.productPrice,
    color: button.dataset.productColor,
  });
  const looksLikeHex = (value) => /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(String(value || '').trim());
  const optionNames = (product) => (Array.isArray(product.options) ? product.options : []).map((option) => cleanText(typeof option === 'string' ? option : option && option.name, 80));
  const variantOptions = (variant) => {
    if (Array.isArray(variant.options)) return variant.options.map((value) => cleanText(value, 120));
    return [variant.option1, variant.option2, variant.option3].filter((value) => value != null).map((value) => cleanText(value, 120));
  };
  const money = (cents, fallback) => {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return fallback || '';
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const locale = document.documentElement.lang || 'en-US';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: amount % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(amount / 100);
    } catch (_error) {
      return fallback || `$${(amount / 100).toFixed(amount % 100 ? 2 : 0)}`;
    }
  };
  const loadProduct = (item) => {
    const pathname = new URL(item.url, window.location.origin).pathname.replace(/\/$/, '');
    if (!productCache.has(pathname)) {
      productCache.set(pathname, fetch(`${pathname}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error('Product unavailable');
        return response.json();
      }));
    }
    return productCache.get(pathname);
  };
  const updateCartCount = async () => {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('.ap-cart').forEach((link) => {
      const count = link.querySelector('span');
      if (count) count.textContent = String(cart.item_count || 0);
      link.setAttribute('aria-label', (link.getAttribute('aria-label') || 'Shopping bag').replace(/\s*\(\d+\)$/, '') + ` (${cart.item_count || 0})`);
    });
    document.dispatchEvent(new CustomEvent('ap:cart:change', { detail: cart }));
  };
  const addVariant = async (variantId) => {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    if (!response.ok) throw new Error('Unable to add');
    await updateCartCount();
  };
  const announce = (drawer, message) => {
    const live = drawer.querySelector('[data-ap-wishlist-live]');
    if (live) live.textContent = message;
  };
  const hydrateItem = async (drawer, article, item) => {
    const color = article.querySelector('[data-ap-wishlist-color]');
    const sizeSlot = article.querySelector('[data-ap-wishlist-size]');
    const price = article.querySelector('[data-ap-wishlist-price]');
    const add = article.querySelector('[data-ap-wishlist-add]');
    const status = article.querySelector('[data-ap-wishlist-status]');
    if (!color || !sizeSlot || !price || !add || !status) return;
    try {
      const product = await loadProduct(item);
      if (!article.isConnected) return;
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const available = variants.filter((variant) => variant.available);
      const names = optionNames(product);
      const sizeIndex = names.findIndex((name) => /size/i.test(name));
      const colorIndex = names.findIndex((name) => /colou?r/i.test(name));
      const firstAvailable = available[0];
      if (colorIndex >= 0 && firstAvailable) {
        const colorName = variantOptions(firstAvailable)[colorIndex];
        if (colorName) {
          color.textContent = colorName;
          color.hidden = false;
        }
      }
      if (!available.length) {
        status.textContent = 'SOLD OUT';
        add.disabled = true;
        return;
      }
      let selectedVariant = sizeIndex < 0 ? firstAvailable : null;
      if (sizeIndex >= 0) {
        const label = document.createElement('label');
        label.className = 'ap-wishlist-item__size';
        const labelText = document.createElement('span');
        labelText.className = 'ap-wishlist-item__size-label';
        labelText.textContent = drawer.dataset.sizeLabel || 'SIZE';
        const select = document.createElement('select');
        select.setAttribute('aria-label', `${drawer.dataset.sizeLabel || 'Size'}: ${item.title}`);
        const prompt = document.createElement('option');
        prompt.value = '';
        prompt.textContent = drawer.dataset.selectSizeLabel || 'SELECT SIZE';
        select.append(prompt);
        const values = [...new Set(available.map((variant) => variantOptions(variant)[sizeIndex]).filter(Boolean))];
        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.append(option);
        });
        select.addEventListener('change', () => {
          selectedVariant = available.find((variant) => variantOptions(variant)[sizeIndex] === select.value) || null;
          add.disabled = !selectedVariant;
          status.textContent = '';
          if (selectedVariant) price.textContent = money(selectedVariant.price, item.price);
        });
        label.append(labelText, select);
        sizeSlot.replaceChildren(label);
      } else {
        add.disabled = false;
        price.textContent = money(firstAvailable.price, item.price);
      }
      add.addEventListener('click', async () => {
        if (!selectedVariant) {
          status.textContent = drawer.dataset.selectSizeLabel || 'SELECT SIZE';
          return;
        }
        const original = add.textContent;
        add.disabled = true;
        add.textContent = 'ADDING…';
        status.textContent = '';
        try {
          await addVariant(selectedVariant.id);
          add.textContent = 'ADDED';
          status.textContent = 'ADDED TO BAG';
          announce(drawer, `${item.title} added to bag`);
          window.setTimeout(() => {
            if (!add.isConnected) return;
            add.textContent = original;
            add.disabled = sizeIndex >= 0 && !selectedVariant;
          }, 1400);
        } catch (_error) {
          add.textContent = original;
          add.disabled = false;
          status.textContent = 'UNABLE TO ADD';
          announce(drawer, `Unable to add ${item.title}`);
        }
      });
    } catch (_error) {
      if (!article.isConnected) return;
      status.textContent = 'PRODUCT DETAILS UNAVAILABLE';
      add.disabled = true;
    }
  };
  const render = (items) => {
    const drawer = document.querySelector('[data-ap-wishlist-drawer]');
    const container = drawer && drawer.querySelector('[data-ap-wishlist-items]');
    const empty = drawer && drawer.querySelector('[data-ap-wishlist-empty]');
    if (!drawer || !container || !empty) return;
    container.replaceChildren();
    empty.hidden = items.length > 0;
    items.forEach((item) => {
      const article = document.createElement('article');
      article.className = 'ap-wishlist-item';
      article.dataset.productId = item.id;

      const imageLink = document.createElement('a');
      imageLink.className = 'ap-wishlist-item__image';
      imageLink.href = item.url;
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        imageLink.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'ap-wishlist-item__placeholder';
        imageLink.append(placeholder);
      }

      const details = document.createElement('div');
      details.className = 'ap-wishlist-item__details';
      const title = document.createElement('a');
      title.className = 'ap-wishlist-item__title';
      title.href = item.url;
      title.textContent = item.title;
      const color = document.createElement('p');
      color.className = 'ap-wishlist-item__color';
      color.dataset.apWishlistColor = '';
      color.hidden = !item.color || looksLikeHex(item.color);
      color.textContent = color.hidden ? '' : item.color;
      const size = document.createElement('div');
      size.dataset.apWishlistSize = '';
      const status = document.createElement('p');
      status.className = 'ap-wishlist-item__status';
      status.dataset.apWishlistStatus = '';
      status.setAttribute('aria-live', 'polite');
      details.append(title, color, size, status);

      const aside = document.createElement('div');
      aside.className = 'ap-wishlist-item__aside';
      const price = document.createElement('p');
      price.className = 'ap-wishlist-item__price';
      price.dataset.apWishlistPrice = '';
      price.textContent = item.price;
      const actions = document.createElement('div');
      actions.className = 'ap-wishlist-item__actions';
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'ap-wishlist-item__add';
      add.dataset.apWishlistAdd = '';
      add.disabled = true;
      add.textContent = drawer.dataset.addToBagLabel || 'ADD TO BAG';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ap-wishlist-item__remove';
      remove.dataset.apWishlistRemove = item.id;
      remove.setAttribute('aria-label', `${drawer.dataset.removeLabel || 'Remove from favorites'}: ${item.title}`);
      remove.textContent = drawer.dataset.removeActionLabel || 'REMOVE';
      actions.append(add, remove);
      aside.append(price, actions);
      article.append(imageLink, details, aside);
      container.append(article);
      hydrateItem(drawer, article, item);
    });
  };
  const sync = () => {
    const items = read();
    const ids = new Set(items.map((item) => item.id));
    document.querySelectorAll('[data-ap-wishlist-toggle]').forEach((button) => {
      const saved = ids.has(button.dataset.productId || '');
      button.setAttribute('aria-pressed', String(saved));
      const label = saved ? button.dataset.removeLabel : button.dataset.addLabel;
      button.setAttribute('aria-label', `${label || ''}: ${button.dataset.productTitle || ''}`.trim());
    });
    document.querySelectorAll('[data-ap-wishlist-count]').forEach((count) => {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-ap-wishlist-drawer-count]').forEach((count) => {
      count.textContent = String(items.length);
    });
    render(items);
  };
  const toggle = (button) => {
    const item = fromButton(button);
    if (!item) return;
    const items = read();
    const index = items.findIndex((saved) => saved.id === item.id);
    if (index >= 0) items.splice(index, 1);
    else items.unshift(item);
    write(items);
  };
  const remove = (id) => write(read().filter((item) => item.id !== id));
  const open = () => {
    const drawer = document.querySelector('[data-ap-wishlist-drawer]');
    if (!drawer || drawer.open) return;
    sync();
    if (typeof drawer.showModal === 'function') drawer.showModal();
    else drawer.setAttribute('open', '');
    document.documentElement.classList.add('ap-wishlist-is-open');
  };
  const close = () => {
    const drawer = document.querySelector('[data-ap-wishlist-drawer]');
    if (!drawer || !drawer.open) return;
    if (typeof drawer.close === 'function') drawer.close();
    else drawer.removeAttribute('open');
    document.documentElement.classList.remove('ap-wishlist-is-open');
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const toggleButton = event.target.closest('[data-ap-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      toggle(toggleButton);
      return;
    }
    const removeButton = event.target.closest('[data-ap-wishlist-remove]');
    if (removeButton) {
      remove(removeButton.dataset.apWishlistRemove || '');
      return;
    }
    if (event.target.closest('[data-ap-wishlist-open]')) {
      event.preventDefault();
      open();
      return;
    }
    if (event.target.closest('[data-ap-wishlist-close]')) close();
  });
  document.addEventListener('ap:wishlist:change', sync);
  document.addEventListener('shopify:section:load', sync);
  document.addEventListener('close', (event) => {
    if (event.target.matches && event.target.matches('[data-ap-wishlist-drawer]')) {
      document.documentElement.classList.remove('ap-wishlist-is-open');
    }
  }, true);
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) sync();
  });
  document.addEventListener('DOMContentLoaded', sync, { once: true });
  sync();
})();
